import {
  FillBase,
  ObjectBase,
  PrintBase,
  PrintTrimData,
  RealObjectBounding, TempFillBase, TempFillDataEntity, TemplateObjType, TempObjectBase,
  TempPrintBase, TempTextBase, TempTextDataEntity,
  TempTextStyle, TempWindowBase, TextBase, TrimImageDataEntityInit, TrimPrintViewDataEntityInit, TrimTempDataEntity,
  VirtualObjectBounding, WindowBase,
} from '../../../model/trim.print-model';
import { PrintDataStructure, PrintItemDataEntity } from '../../../../models/sift/json/print-model';
import { TrimTempModel } from '../../../model/trim.temp-model';
import { ColorCodeConverter } from '../../../../utilities/colorcode-converter';
import PixelConverter, { WH } from '../../../../utilities/pixel-converter';
import { DateFormatter } from '../../../../utilities/date-formatter';
import { Exif } from '../../../manager/image-edit/trim.editable-image';
import { TrimDataManager } from '../../../manager/data/trim-data.manager';

export type TrimLaboOrderImage = {
  id: string,
  virtual: {
    width: number,
    height: number,
  },
  real: {
    width: number,
    height: number,
  }
  exif: Exif;
}

export type TrimImageBase = {
  id: string,
  width: number,
  height: number,
  exif: Exif;
}

export class TrimLaboOrderPrintModel {
  constructor(
    public shopOrderId: string,
    public kijshopCd: string,
    public orderId: string,
    public image: TrimLaboOrderImage,
    public printTrimData: PrintTrimData,
    public structure: PrintDataStructure,
    public item: PrintItemDataEntity,
    public base: PrintBase,
  ) {}
}

export class TrimLaboOrderPrintModelCreator {

  private orderId: string = '';
  private image: TrimLaboOrderImage | null = null;
  private printTrimData: PrintTrimData | null = null;
  private structure: PrintDataStructure | null = null;

  constructor() {}

  /**
   * jsonのモデルとかテンプレのモデルを一撃で生成
   * @param {{kijshopCd: string, shopOrderId: string, orderId: string, image: TrimLaboOrderImage, structure: PrintDataStructure}} param
   * @returns {Promise<TrimLaboOrderPrintModel>}
   */
  public async create(param: {
    kijshopCd: string,
    shopOrderId: string,
    orderId: string,
    image: TrimImageBase,
    structure: PrintDataStructure
  }) {
    const { kijshopCd, shopOrderId, orderId, image, structure } = param;
    const { width, height, id, exif } = image;
    this.orderId = orderId;
    this.image = {
      id,
      virtual: this.resizeImageMax({width, height}),
      real: {width, height},
      exif,
    };
    this.structure = structure ?? null;
    const item = structure.item?.[orderId];
    if (!item) throw new Error('not found itemModel !!!');
    const temp = await this.loadTemplate(item);
    if (!temp) throw new Error('not found tempModel !!!');
    const _temp = this.templateFormatter(temp, item);
    const printTrimData = await this.createPrintTrimData({ item, temp: _temp });
    const base = await this.createPrintBase(item, _temp);
    return new TrimLaboOrderPrintModel(
      shopOrderId,
      kijshopCd,
      orderId,
      this.image,
      printTrimData,
      structure,
      item,
      base
    );
  };

  // - 以下はメイン機能をほぼそのまま流用のためFix時は注意 -
  private templateFormatter(temp: TrimTempModel, item: PrintItemDataEntity): TempPrintBase {
    const base = temp.layout.print?.[0].base?.[0];
    const frame = base.frame;
    const date = this.getDate(item) ?? '';
    if (!base || !frame?.length) {
      throw new Error('無効なテンプレートです !!!');
    }
    const id = this.orderId;

    const tempBase = new TempPrintBase(
      id,
      { width: Number(base?.$.width), height: Number(base?.$.height) },
      [],
    );
    frame.forEach((v, i) => {
      const _window = v.window?.[0];
      const _object = _window?.object?.[0];
      const _fill = _window?.fill?.[0];
      const isText = _object?.$.type === 'string';
      const textVal = _object?.string?.[0].text?.[0].trim() ? _object?.string?.[0].text?.[0] : date;

      const objType: TemplateObjType = (() => {
        if (isText) {
          return _object?.string?.[0].text?.[0].trim() ? 'tempText' : 'dateText';
        }
        return 'template';
      })()

      const text = isText ? new TempTextBase(
        textVal,
        Number(_object?.string?.[0].$.point ?? '90'),
        _object?.string?.[0].$.face ?? '',
        Number(_object?.antialias?.[0].$.enable ?? '1'),
        new TempTextStyle(
          {
            width: Number(_object?.edge?.[0].$.width ?? '0.3'),
            color: ColorCodeConverter.Hex2Color(_object?.edge?.[0].$.color ?? '0xffffff') ?? '#ffffff',
          },
          '#000000',
          false,
          false,
        ),
      ) : undefined;
      const object = _object ? new TempObjectBase(
        objType,
        _object?.$.id,
        _object?.$.type,
        text,
      ) : undefined;
      const fill = _fill ? new TempFillBase(
        Number(_fill.$.enable),
        ColorCodeConverter.Hex2Color(_fill.$.color ?? '0xffffff') ?? '#ffffff',
      ) : undefined;
      const window = new TempWindowBase(
        id,
        i + 1,
        {
          wh: { width: Number(_window?.$.width), height: Number(_window?.$.height) },
          pos: { x: Number(_window?.$.x), y: Number(_window?.$.y) },
        },
        _window?.$.attribute ?? '24',
        object,
        fill,
      );

      tempBase.addWindow(window);
    });

    return tempBase;
  }

  private async getPrintTrimData(item: PrintItemDataEntity, temp: TempPrintBase): Promise<void> {
    if (!this.image) return;

    const printTrimData = await this.createPrintTrimData({ item, temp });
    this.printTrimData = printTrimData;
  }

  private async createPrintBase(item: PrintItemDataEntity, temp: TempPrintBase): Promise<PrintBase> {

    const id = this.orderId;
    if (!this.printTrimData) {
      await this.getPrintTrimData(item, temp);
    }
    const date = this.getDate(item);
    const printBase = new PrintBase(
      id,
      { width: temp.wh.width, height: temp.wh.height },
      [],
    );
    temp.window.forEach((v, i) => {
      if (!this.image) return;
      if (v.object) {
        switch (v.object?.objType) {
          case 'template':
            const { wh, pos } = v.bound;
            TrimDataManager.setAdditionalValue = wh.width <= wh.height ? wh.width : wh.height;
            printBase.addWindow(new WindowBase(
              this.image.id,
              v.depth,
              new RealObjectBounding({ width: wh.width, height: wh.height }, { x: pos.x, y: pos.y }),
              new VirtualObjectBounding({
                width: wh.width,
                height: wh.height,
              }, { x: pos.x, y: pos.y }),
              new ObjectBase(
                this.image.id,
                {
                  pos: { x: 0, y: 0 },
                  wh: { width:  0, height: 0 },
                },
                0,
                'image',
                v.object?.type,
              ),
            ));
            break;
          case 'dateText':
            printBase.addWindow(new WindowBase(
              this.image.id,
              v.depth,
              new RealObjectBounding({ width: v.bound.wh.width, height: v.bound.wh.height }, {
                x: v.bound.pos.x,
                y: v.bound.pos.y,
              }),
              new VirtualObjectBounding({
                width: v.bound.wh.width,
                height: v.bound.wh.height,
              }, { x: v.bound.pos.x, y: v.bound.pos.y }),
              new ObjectBase(
                this.image.id,
                { pos: { x: 0, y: 0 }, wh: { width: 0, height: 0 } },
                0,
                'dateText',
                v.object?.type,
                new TextBase(
                  this.image.id,
                  v.object.text?.value ?? '',
                  Number(v.object?.text?.point ?? '90'),
                  (v.object?.text?.font ?? 'ＭＳ Ｐゴシック'),
                  (v.object?.text?.style ?? new TempTextStyle()),
                ),
              ),
            ));
            break;
            case 'tempText':
            printBase.addWindow(new WindowBase(
              this.image.id,
              v.depth,
              new RealObjectBounding({ width: v.bound.wh.width, height: v.bound.wh.height }, {
                x: v.bound.pos.x,
                y: v.bound.pos.y,
              }),
              new VirtualObjectBounding({
                width: v.bound.wh.width,
                height: v.bound.wh.height,
              }, { x: v.bound.pos.x, y: v.bound.pos.y }),
              new ObjectBase(
                this.image.id,
                { pos: { x: 0, y: 0 }, wh: { width: 0, height: 0 } },
                0,
                'tempText',
                v.object?.type,
                new TextBase(
                  this.image.id,
                  v.object.text?.value ?? '',
                  Number(v.object?.text?.point ?? '90'),
                  (v.object?.text?.font ?? 'ＭＳ Ｐゴシック'),
                  (v.object?.text?.style ?? new TempTextStyle()),
                ),
              ),
            ));
        }
      } else if (v.fill) {
          if (!this.image) return;
          if (!v.fill) return;

          const { wh, pos } = v.bound;
        printBase.addWindow(new WindowBase(
          this.image.id,
          v.depth,
          new RealObjectBounding({ width: wh.width, height: wh.height }, { x: pos.x, y: pos.y }),
          new VirtualObjectBounding({
            width: wh.width,
            height: wh.height,
          }, { x: pos.x, y: pos.y }),
          undefined,
          new FillBase(
            (v.fill.enable ?? 1),
            ColorCodeConverter.Hex2Color(v.fill.color) ?? '#FFFFFF',
          ),
        ));
      }

    });

    return printBase;
  }

  private async createPrintTrimData(
    args: { item: PrintItemDataEntity, temp: TempPrintBase },
  ): Promise<PrintTrimData> {
    return new Promise((resolve, reject) => {
      const { item, temp } = args;
      const date = this.getDate(item);
      const id = `${this.orderId}_${this.image?.id ?? ''}`;

      const textData = temp.getDateTextWindow();
      const tempTexts = temp.getTextWindow();
      const tempFrame = temp.getTempWindow();
      const fills = temp.getFillWindow();

      const tmpData = new TrimTempDataEntity(
        { width: temp.wh.width, height: temp.wh.height },
        textData ? {
          depth: textData.depth,
          x: textData.bound.pos.x,
          y: textData.bound.pos.y,
          width: textData.bound.wh.width,
          height: textData.bound.wh.height,
          attribute: textData.attribute,
          edge: {
            color: ColorCodeConverter.Color2Hex(textData.object?.text?.style.outline.color ?? '#ffffff'),
            width: textData.object?.text?.style.outline.width ?? 0.3,
          },
          antialiasEnable: textData.object?.text?.antialiasEnable ?? 1,
          style: { face: textData.object?.text?.font ?? 'ＭＳ ゴシック', point: textData.object?.text?.point ?? 90 },
          value: date ? date : '',
        } : undefined,
      );
      tmpData.setTempTexts(tempTexts.map((data): TempTextDataEntity => {
        return {
          depth: data.depth,
          x: data.bound.pos.x,
          y: data.bound.pos.y,
          width: data.bound.wh.width,
          height: data.bound.wh.height,
          attribute: data.attribute,
          edge: {
            color: ColorCodeConverter.Color2Hex(data.object?.text?.style.outline.color ?? '#ffffff'),
            width: data.object?.text?.style.outline.width ?? 0.3,
          },
          antialiasEnable: data.object?.text?.antialiasEnable ?? 1,
          style: { face: data.object?.text?.font ?? 'ＭＳ ゴシック', point: data.object?.text?.point ?? 90 },
          value: data.object?.text?.value ?? '',
        }
      }));
      tmpData.setFills(fills.map((data): TempFillDataEntity => {
        return {
          depth: data.depth,
          x: data.bound.pos.x,
          y: data.bound.pos.y,
          width: data.bound.wh.width,
          height: data.bound.wh.height,
          attribute: data.attribute,
          enable: data.fill?.enable ?? 0,
          color: ColorCodeConverter.Color2Hex(data.fill?.color ?? '#ffffff'),
        }
      }));
      const result = new PrintTrimData(
        id,
        {
          id,
          itemId: item.id,
          config: {space: item.productData?.itemSetting.space ?? 0, trimBaseType: item.productData?.meta?.trimBaseType ?? 1},
          temp: tmpData,
          image: new TrimImageDataEntityInit(this.image?.id ?? '', tempFrame?.depth ?? 1, tempFrame ? new RealObjectBounding(tempFrame?.bound.wh, tempFrame?.bound.pos) : undefined),
          texts: [],
          stamps: [],
          view: new TrimPrintViewDataEntityInit(),
        }
      );
      this.printTrimData = result;
      resolve(result);
    });
  }

  private getDate(item: PrintItemDataEntity) {
    const isDate = item.productData?.meta?.datePrint;
    if (!isDate) return null;
    const date = this.image?.exif.createDate;
    if (!date) return null;
    return DateFormatter.date2str(date);
  }

  private loadTemplate = (item: PrintItemDataEntity) => new Promise<TrimTempModel | null>((resolve) => {
    const image = item.images.find(v => v.id === this.image?.id);
    if (!image) {
      resolve(null);
      return;
    }
    const nextRotate = !Number(image.rotate) ? '1' : '2';
    const printDateFlg = image.datePrint;
    const template = this.structure?.getTemplate(item.id, nextRotate, printDateFlg);
    if (template) {
      resolve(template.template);
    } else {
      resolve(null);
    }
  });

  private resizeImageMax(image: { width: number; height: number }, maxSize: number = 1000) {
    const { width, height } = image;

    if (!width || !height) return { width: 0, height: 0 };

    /*
     * 先に除算すると 5472 / (5472 / 1000) が 999.9999999999999 になり 1px 小さくなる。
     * サムネイル生成(compressorjs)と結果を揃えるため乗算を先に行う。
     */
    const max = Math.max(width, height);
    return {
      width: Math.floor(width * maxSize / max),
      height: Math.floor(height * maxSize / max),
    };
  }
}
